import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useFilteredList } from "@/hooks/useFilteredList";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { matchesScoreRange } from "@/lib/candidateFilters";
import { getStoredToken, isSuperAdmin, parseJwt } from "@/types";
import type { AuthUser, Candidate, Position } from "@/types";

const VISIBLE_STAGES_KEY = "pipeline-visible-stages";

const STAGES = [
  { key: "new", label: "New", accent: "bg-gray-100 border-gray-300" },
  { key: "screened", label: "Screened", accent: "bg-blue-50 border-blue-200" },
  { key: "shortlisted", label: "Shortlisted", accent: "bg-emerald-50 border-emerald-200" },
  { key: "interviewed", label: "Interviewed", accent: "bg-violet-50 border-violet-200" },
  { key: "offered", label: "Offered", accent: "bg-amber-50 border-amber-200" },
  { key: "hired", label: "Hired", accent: "bg-emerald-100 border-emerald-300" },
  { key: "rejected", label: "Rejected", accent: "bg-rose-50 border-rose-200" },
] as const;

type StageKey = (typeof STAGES)[number]["key"];

function effectiveStage(c: Candidate): StageKey {
  const explicit = (c.current_stage || "") as StageKey;
  // Respect explicit moves past initial screening (incl. an explicit "rejected").
  if (explicit && explicit !== "new" && explicit !== "screened") return explicit;
  // Otherwise, surface rejection flags into the Rejected column.
  if (c.auto_rejected === true || c.recommendation === "reject") return "rejected";
  return explicit || "new";
}

export function PipelinePage({
  positions,
  currentUser,
  onViewProfile,
}: {
  positions: Position[];
  currentUser?: AuthUser;
  onViewProfile: (id: number) => void;
}) {
  const queryClient = useQueryClient();
  const [filterPositionId, setFilterPositionId] = useState<number | null>(null);
  const [filterOrgId, setFilterOrgId] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterRec, setFilterRec] = useState<"all" | "strong_hire" | "consider" | "reject">("all");
  const [filterScore, setFilterScore] = useState<"all" | "90+" | "80-89" | "60-79" | "<60">("all");
  const [filterAutoRejected, setFilterAutoRejected] = useState<"all" | "yes" | "no">("all");

  const { data: orgsData } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.listOrganizations(),
    enabled: isSuperAdmin(currentUser),
  });
  const orgs = orgsData?.organizations ?? [];
  const [savedStages, setSavedStages] = useLocalStorage<Partial<Record<StageKey, boolean>>>(
    VISIBLE_STAGES_KEY, {}
  );
  const visibleStages = useMemo(
    () => STAGES.reduce<Record<StageKey, boolean>>((acc, s) => {
      acc[s.key] = savedStages[s.key] !== false;
      return acc;
    }, {} as Record<StageKey, boolean>),
    [savedStages]
  );
  const setVisibleStages = (stages: Record<StageKey, boolean>) => setSavedStages(stages);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const columnsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!columnsMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (!columnsMenuRef.current?.contains(e.target as Node)) setColumnsMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [columnsMenuOpen]);

  const visibleStageList = STAGES.filter((s) => visibleStages[s.key]);
  const hiddenCount = STAGES.length - visibleStageList.length;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.candidates({ positionId: filterPositionId ?? undefined }),
    queryFn: () => api.listCandidates({ positionId: filterPositionId ?? undefined }),
  });
  const candidates: Candidate[] = data?.candidates ?? [];

  const candidateFilter = useCallback(
    (c: Candidate, q: string) => {
      if (q && !(
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.filename?.toLowerCase().includes(q)
      )) return false;
      if (filterRec !== "all" && c.recommendation !== filterRec) return false;
      if (!matchesScoreRange(c.match_score, filterScore)) return false;
      if (filterAutoRejected !== "all") {
        const isRejected = c.auto_rejected === true;
        if (filterAutoRejected === "yes" && !isRejected) return false;
        if (filterAutoRejected === "no" && isRejected) return false;
      }
      if (filterOrgId !== null && c.org_id !== filterOrgId) return false;
      return true;
    },
    [filterRec, filterScore, filterAutoRejected, filterOrgId]
  );

  const { filtered, search, setSearch } = useFilteredList(candidates, candidateFilter);

  const grouped = useMemo(() => {
    const map: Record<StageKey, Candidate[]> = {
      new: [], screened: [], shortlisted: [], interviewed: [], offered: [], hired: [], rejected: [],
    };
    for (const c of filtered) {
      const stage = effectiveStage(c);
      if (map[stage]) map[stage].push(c);
    }
    return map;
  }, [filtered]);

  const hasActiveFilters =
    !!search.trim() ||
    filterPositionId !== null ||
    filterOrgId !== null ||
    filterRec !== "all" ||
    filterScore !== "all" ||
    filterAutoRejected !== "all";
  const activeFilterCount =
    (search.trim() ? 1 : 0) +
    (filterPositionId !== null ? 1 : 0) +
    (filterOrgId !== null ? 1 : 0) +
    (filterRec !== "all" ? 1 : 0) +
    (filterScore !== "all" ? 1 : 0) +
    (filterAutoRejected !== "all" ? 1 : 0);
  const clearFilters = () => {
    setSearch("");
    setFilterPositionId(null);
    setFilterOrgId(null);
    setFilterRec("all");
    setFilterScore("all");
    setFilterAutoRejected("all");
  };

  const moveTo = useMutation({
    mutationFn: ({ candidate_id, stage, notes }: { candidate_id: number; stage: StageKey; notes?: string }) =>
      api.runLifecycleAgent({ candidate_id, action: "move_to", stage, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
    },
  });

  const handleDrop = (candidateId: number, targetStage: StageKey) => {
    const c = candidates.find((x) => x.id === candidateId);
    if (!c) return;
    if (effectiveStage(c) === targetStage) return;
    moveTo.mutate({ candidate_id: candidateId, stage: targetStage });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">Pipeline</h2>
          <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">
            {filtered.length}
            {hasActiveFilters && filtered.length !== candidates.length ? ` / ${candidates.length}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              type="text"
              placeholder="Search name, email, file…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-btn border border-border-card bg-white py-2 pl-9 pr-3 text-sm text-near-black focus:outline-none focus:shadow-focus"
            />
          </div>
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className={[
              "flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors",
              filtersOpen || hasActiveFilters
                ? "bg-near-black text-white"
                : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
            ].join(" ")}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rausch text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          <div ref={columnsMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setColumnsMenuOpen((v) => !v)}
              className="relative flex h-9 w-9 items-center justify-center rounded-btn border border-border-card bg-white text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
              title="Show / hide columns"
              aria-label="Show or hide pipeline columns"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              {hiddenCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rausch px-1 text-[9px] font-bold text-white">
                  {hiddenCount}
                </span>
              )}
            </button>
            {columnsMenuOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-card border border-border-card bg-white p-2 shadow-card">
                <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.32px] text-text-secondary">
                  Columns
                </div>
                {STAGES.map((s) => (
                  <label key={s.key} className="flex cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-xs text-near-black hover:bg-surface-secondary">
                    <input
                      type="checkbox"
                      checked={!!visibleStages[s.key]}
                      onChange={(e) =>
                        setVisibleStages({ ...visibleStages, [s.key]: e.target.checked })
                      }
                      className="h-3.5 w-3.5 rounded border-border-card"
                    />
                    <span className="flex-1">{s.label}</span>
                    <span className="text-[10px] tabular-nums text-text-secondary">
                      {grouped[s.key]?.length ?? 0}
                    </span>
                  </label>
                ))}
                <div className="mt-1 flex items-center justify-between border-t border-surface-secondary px-2 pt-2 text-[10px] text-text-secondary">
                  <button
                    type="button"
                    onClick={() => setVisibleStages(STAGES.reduce<Record<StageKey, boolean>>((acc, s) => { acc[s.key] = true; return acc; }, {} as Record<StageKey, boolean>))}
                    className="font-medium text-rausch hover:underline"
                  >
                    Show all
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisibleStages(STAGES.reduce<Record<StageKey, boolean>>((acc, s) => { acc[s.key] = false; return acc; }, {} as Record<StageKey, boolean>))}
                    className="font-medium hover:text-near-black"
                  >
                    Hide all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      </div>

      {filtersOpen && (
        <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="rounded-card bg-white p-6 shadow-card">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {isSuperAdmin(currentUser) && orgs.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Organization</label>
                <select
                  value={filterOrgId ?? ""}
                  onChange={(e) => setFilterOrgId(e.target.value ? Number(e.target.value) : null)}
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                >
                  <option value="">All organizations</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Position</label>
              <select
                value={filterPositionId ?? ""}
                onChange={(e) => setFilterPositionId(e.target.value ? Number(e.target.value) : null)}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
              >
                <option value="">All positions</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Recommendation</label>
              <select
                value={filterRec}
                onChange={(e) => setFilterRec(e.target.value as typeof filterRec)}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
              >
                <option value="all">All</option>
                <option value="strong_hire">Strong hire</option>
                <option value="consider">Consider</option>
                <option value="reject">Reject</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Score range</label>
              <select
                value={filterScore}
                onChange={(e) => setFilterScore(e.target.value as typeof filterScore)}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
              >
                <option value="all">All scores</option>
                <option value="90+">90+ (Excellent)</option>
                <option value="80-89">80–89 (Strong)</option>
                <option value="60-79">60–79 (Consider)</option>
                <option value="<60">Below 60 (Reject)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Auto-rejected</label>
              <select
                value={filterAutoRejected}
                onChange={(e) => setFilterAutoRejected(e.target.value as typeof filterAutoRejected)}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
              >
                <option value="all">All</option>
                <option value="yes">Auto-rejected only</option>
                <option value="no">Hide auto-rejected</option>
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-rausch hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Scrollable kanban area */}
      <div className="flex flex-1 min-h-0 overflow-x-auto">
        {isLoading ? (
          <p className="m-auto text-text-secondary">Loading…</p>
        ) : visibleStageList.length === 0 ? (
          <div className="m-auto rounded-card border border-dashed border-border-card bg-white px-6 py-10 text-center text-sm text-text-secondary">
            All columns are hidden. Use the columns menu (above) to show at least one stage.
          </div>
        ) : (
          <div className="flex h-full gap-3 p-4">
            {visibleStageList.map((stage) => (
              <StageColumn
                key={stage.key}
                stage={stage}
                candidates={grouped[stage.key]}
                onDrop={handleDrop}
                onViewProfile={onViewProfile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StageColumn({
  stage,
  candidates,
  onDrop,
  onViewProfile,
}: {
  stage: (typeof STAGES)[number];
  candidates: Candidate[];
  onDrop: (candidateId: number, stageKey: StageKey) => void;
  onViewProfile: (id: number) => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const id = Number(e.dataTransfer.getData("text/candidate-id"));
        if (id) onDrop(id, stage.key);
      }}
      className={[
        "flex h-full w-72 shrink-0 flex-col rounded-card border-2 transition-colors",
        stage.accent,
        over ? "border-near-black ring-2 ring-near-black/20" : "",
      ].join(" ")}
    >
      <div className="flex items-center justify-between border-b border-border-card px-4 py-3">
        <h3 className="text-sm font-semibold text-near-black">{stage.label}</h3>
        <span className="rounded-badge bg-white px-2 py-0.5 text-[10px] font-bold tabular-nums text-text-secondary">
          {candidates.length}
        </span>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2">
        {candidates.length === 0 ? (
          <p className="px-2 py-4 text-center text-[10px] italic text-text-secondary">
            Drop a candidate here
          </p>
        ) : (
          candidates.map((c) => <PipelineCard key={c.id} candidate={c} onView={onViewProfile} />)
        )}
      </div>
    </div>
  );
}

function PipelineCard({
  candidate,
  onView,
}: {
  candidate: Candidate;
  onView: (id: number) => void;
}) {
  const isSuper = isSuperAdmin(parseJwt(getStoredToken() ?? ""));
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/candidate-id", String(candidate.id));
        e.dataTransfer.effectAllowed = "move";
      }}
      className="cursor-grab rounded-btn bg-white p-3 shadow-sm transition-shadow hover:shadow-card active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-near-black">
            {candidate.name || "Unknown"}
          </p>
          {isSuper && candidate.org_name && (
            <p className="truncate text-[9px] text-indigo-600">{candidate.org_name}</p>
          )}
        </div>
        <span className={`shrink-0 rounded-badge px-1.5 py-0.5 text-[9px] font-bold tabular-nums ${
          candidate.match_score >= 80 ? "bg-emerald-100 text-emerald-800"
          : candidate.match_score >= 60 ? "bg-amber-100 text-amber-800"
          : "bg-rose-100 text-error-text"
        }`}>
          {Math.round(candidate.match_score)}
        </span>
      </div>
      {candidate.auto_rejected && (
        <p className="mt-1 text-[9px] font-medium text-error-text">⚠ Auto-rejected by filter</p>
      )}
      <button
        onClick={() => onView(candidate.id)}
        className="mt-2 w-full rounded-btn bg-surface-secondary px-2 py-1 text-[10px] font-medium text-text-focused transition-colors hover:bg-near-black hover:text-white"
      >
        Open profile
      </button>
    </div>
  );
}
